import {
  Card,
  CardTitle,
  EmptyState,
  ErrorMessage,
  LoadingState,
  Subtitle,
} from '@vrzl/ui';
import { useSetAtom } from 'jotai';
import { FileText, Search } from 'lucide-react';
import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router';

import { CriteriaSearch, useCriteriaSearch } from '@/domains/criteria';
import { EventHeader, RetryExtractionButton, useEvent } from '@/domains/events';

import { useDocuments } from '../api/queries';
import type { DocumentListItem } from '../api/types';
import {
  countByStatus,
  DOCUMENT_FILTER_PARAM,
  filterByStatus,
  parseDocumentFilter,
  type DocumentFilter,
} from '../helpers/documentFilter';
import { canStartExtraction } from '../helpers/indexingStatus';
import { documentDialogAtom } from '../state/documentDialog.atom';

import { DocumentsFilter } from './documentsFilter';
import { DocumentSummary } from './documentSummary';

const EXTRACTION_RUNNING = new Set(['QUEUED', 'IN_PROGRESS']);

// Op moduleniveau zodat useCriteriaSearch zijn memo kan hergebruiken.
const getName = (doc: DocumentListItem) => doc.name;

export function Documents() {
  const { eventId } = useParams();
  const { data: event } = useEvent(eventId ?? '');
  const { data, isPending, error } = useDocuments(eventId ?? '');
  const setDocDialog = useSetAtom(documentDialogAtom);

  // Het statusfilter staat in de URL (?status=…), zodat een link er direct naartoe kan
  // wijzen. De zoekterm blijft lokaal en reset bij verversen.
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = parseDocumentFilter(searchParams.get(DOCUMENT_FILTER_PARAM));
  const handleFilterChange = (next: DocumentFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete(DOCUMENT_FILTER_PARAM);
    else params.set(DOCUMENT_FILTER_PARAM, next);
    setSearchParams(params, { replace: true });
  };
  const documents = useMemo(() => data?.items ?? [], [data]);
  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    results: searchedDocuments,
  } = useCriteriaSearch(documents, { getName });
  // Aantallen altijd over alle documenten, ook tijdens zoeken.
  const filterCounts = useMemo(() => countByStatus(documents), [documents]);
  const visibleDocuments = useMemo(
    () => filterByStatus(searchedDocuments, filter),
    [searchedDocuments, filter],
  );

  const showStartExtraction =
    event !== undefined &&
    data !== undefined &&
    canStartExtraction(data.items) &&
    (event.extractionStatus === null || !EXTRACTION_RUNNING.has(event.extractionStatus));

  return (
    <div>
      {event && (
        <EventHeader
          event={event}
          documents={documents}
          hideDocumentsLink
          actions={showStartExtraction && <RetryExtractionButton eventId={event.id} />}
        />
      )}

      {isPending && <LoadingState />}
      {error && <ErrorMessage error={error} />}

      {data && documents.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <DocumentsFilter value={filter} counts={filterCounts} onChange={handleFilterChange} />
          <CriteriaSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Zoek op document"
            className="w-full max-w-xs"
          />
        </div>
      )}

      {data && (
        <Card padding="lg">
          <CardTitle>Documenten</CardTitle>
          <Subtitle className="mt-1">
            Alle documenten die bij dit evenement horen ({data.total} {data.total === 1 ? 'document' : 'documenten'})
          </Subtitle>

          {documents.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="Geen documenten gevonden"
            />
          ) : visibleDocuments.length === 0 ? (
            <EmptyState
              icon={Search}
              title="Geen documenten gevonden"
              description="Geen documenten voor dit filter of deze zoekterm. Pas je zoekterm aan of kies 'Alles'."
            />
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              {visibleDocuments.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() =>
                    setDocDialog({ eventId: eventId ?? '', docId: doc.id })
                  }
                  className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-muted"
                >
                  <DocumentSummary document={doc} />
                </button>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
